<script setup>
import { ref, computed, inject, watch } from 'vue'
import Standings from './Standings.vue'
import AddEntryToGroup from '../TournamentForm/AddEntryToGroup.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import SaveButton from '@/components/SaveButton.vue'
import { useMainStore } from '@/stores/main'
import { useI18n } from 'vue-i18n'
import { groupName, goToTournament } from '@/services/utils'
import * as Server from '@/services/Server'
import * as Alerts from '@/services/Alerts'
import GroupRoundsOverview from './GroupRoundsOverview.vue'
import ActionRecalculateStandings from '../TournamentForm/ActionRecalculateStandings.vue'
import StandingsTeams from './StandingsTeams.vue'

const tournament = inject('tournament')
const editMode = inject('editMode')
const currents = inject('currents')

const { t } = useI18n()
const loading = ref(false)
const store = useMainStore()
const group = defineModel()

const confirmDialog = ref(false)
const entryToRemove = ref(null)
const standingsRef = ref(null)
const showTeamsStanding = ref(false)
const toggleShowTeamsStanding = () => {
  showTeamsStanding.value = !showTeamsStanding.value
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
}

const originalEntries = ref([])

watch(() => group.value?.id, (id) => {
  if (id) {
    originalEntries.value = JSON.parse(JSON.stringify(group.value.group_entries || []))
  }
}, { immediate: true })

const hasChanges = computed(() => {
  const current = group.value?.group_entries || []
  return JSON.stringify(originalEntries.value) !== JSON.stringify(current)
})

const askRemoveFromGroup = (entry) => {
  entryToRemove.value = entry
  confirmDialog.value = true
}

const removeFromGroup = async () => {
  const entry = entryToRemove.value
  if (!entry) {
    return
  }

  loading.value = true
  try {
    const path = store.apiPath.remove_group_entry.replace('{group}', group.value.id)
    await Server.post(path, { entry_id: entry.entry_id })
    group.value.group_entries = group.value.group_entries.filter(e => e.entry_id !== entry.entry_id)

  } catch (error) {
    console.error('Error adding entry:', error)
    const message = error.response?.data?.message || 'An error occurred'
    Alerts.error(message)
  } finally {
    loading.value = false
  }

  entryToRemove.value = null
}

const navigateToGroup = (grp) => {
  goToTournament(tournament.value.slug, {
    s: currents.value.season?.season_number,
    l: currents.value.level?.level_number,
    st: currents.value.stage?.stage_number,
    g: grp.group_number,
  })
}

const saveEntries = async () => {
  loading.value = true
  try {
    const path = store.apiPath.update_group_entries.replace('{group}', group.value.id)
    const entriesData = group.value.group_entries.map(entry => ({ ...entry }))
    await Server.post(path, { entries: entriesData })
    // ask standings child to re-sort now that entries changed
    standingsRef.value?.sortEntries?.()
    // update baseline after successful save
    originalEntries.value = JSON.parse(JSON.stringify(group.value.group_entries || []))
  } catch (error) {
    console.error('Error saving entries:', error)
    const message = error.response?.data?.message || 'An error occurred'
    Alerts.error(message)
  } finally {
    loading.value = false
  }
}
</script>


<template>
  <v-card class="mb-8">
    <v-card-title class="bg-alt">
      <div v-if="currents.stage.groups.length == 1 && !$route.query.g" class="d-flex align-center ga-2">
        <template v-if="currents.stage?.type == 'ffa' && showTeamsStanding">
          <v-icon size="sm">mdi-shield-star-outline</v-icon>
          {{ $t('tournaments.teamStandings') }}
        </template>
        <template v-else>
          <v-icon size="sm">mdi-podium</v-icon>
          {{  $t('label.standings') }}
        </template>
      </div>
      <template v-else>
        <span class="cursor-pointer" @click="navigateToGroup(group)">{{ groupName(group) }}</span>
      </template>
    </v-card-title>

    <v-card-text class="pa-0">
      <template v-if="!showTeamsStanding">
        <Standings
          ref="standingsRef"
          v-model:group_entries="group.group_entries"
          @remove-from-group="askRemoveFromGroup"
        />

        <!-- Buttons -->
        <div class="d-block d-md-flex justify-space-between mt-6 px-4">
          <div v-if="editMode">
            <div class="mb-2">
              <AddEntryToGroup
                v-model="group"
                class="me-4"
              />
            </div>
            <div v-if="group.rounds?.length > 0 && group.group_entries?.length > 0">
              <ActionRecalculateStandings v-if="currents.stage?.type == 'ffa'"
                v-model="group"
              />
            </div>
          </div>
          <div v-if="editMode" class="d-block d-sm-flex justify-space-between mt-6 mt-md-0">
            <SaveButton
              :has-changes="hasChanges"
              :loading="loading"
              @save="saveEntries"
              class="mt-4 mt-sm-0"
            >
              {{ t('common.save') }}
            </SaveButton>
          </div>
        </div>
      </template>


      <StandingsTeams
        v-else
        v-model:group_entries="group.group_entries"
      />

      <div class="my-4 px-4" v-if="currents.stage?.type == 'ffa' && tournament.settings?.useTeams">
        <v-btn color="secondary" variant="tonal" @click="toggleShowTeamsStanding">
          {{ showTeamsStanding ? $t('tournaments.showMainStandings') : $t('tournaments.showTeamStandings') }}
        </v-btn>
      </div>

    </v-card-text>
  </v-card>


  <GroupRoundsOverview
    v-if="group.rounds"
    v-model="group"
  />

  <ConfirmDialog
    v-model="confirmDialog"
    :title="t('tournaments.removeFromGroup')"
    :text="t('tournaments.removeFromGroupConfirm')"
    :confirmText="t('common.remove')"
    :cancelText="t('common.cancel')"
    confirmColor="error"
    @confirm="removeFromGroup"
  />
</template>
